import { useTheme } from '@emotion/react';
import { Bookshelf, Check, DegreeHat, Fire, SwitchThemes, Time, Translate } from '@icon-park/react';
import { Fragment, memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { THEME_MODES } from '@didaskill/domain/configurations/constants';
import { USER_COURSE_ACCESS_TYPES } from '@didaskill/domain/users/constants';
import { LineSeparator, SELECT_SIZES, Select, Tag, Typography } from '@didaskill/ui/design-system';
import { Truncate } from '@didaskill/ui/utils';

import { useProfileActiveStatus } from '../../../hooks/useProfileActiveStatus';
import { LOCALE_OPTIONS, THEME_MODE_OPTIONS } from './constants';
import messages from './ProfileDetails.messages';
import * as styles from './ProfileDetails.styles';

export type ProfileDetailsProps = {
  nbMaxStreaks: number;
  lastActiveAt: string;
  courses: {
    course: {
      title: string;
    };
    accessType: USER_COURSE_ACCESS_TYPES;
  }[];
  studentRef?: string;
  isActiveUser?: boolean;
  isInternationalizationEnabled?: boolean;
  onLocaleChange?: (newLocale: string) => void;
  locale?: string;
  themeMode?: THEME_MODES;
  onThemeModeChange?: (newThemeMode: THEME_MODES) => void;
  isThemeModeEnabled?: boolean;
};

export const ProfileDetailsBase = ({
  nbMaxStreaks,
  lastActiveAt,
  courses,
  studentRef,
  isActiveUser = false,
  isInternationalizationEnabled = false,
  onLocaleChange = null,
  locale = 'fr',
  themeMode,
  onThemeModeChange,
  isThemeModeEnabled = false,
}: ProfileDetailsProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const { message } = useProfileActiveStatus(lastActiveAt);

  const currentLocaleOption =
    LOCALE_OPTIONS.find((option) => option.value === locale) || LOCALE_OPTIONS[0];
  const currentThemeModeOption =
    THEME_MODE_OPTIONS.find((option) => option.value === themeMode) || THEME_MODE_OPTIONS[0];

  const handleThemeModeChange = useCallback(
    (option: THEME_MODES) => {
      if (option && onThemeModeChange) {
        onThemeModeChange(option);
      }
    },
    [onThemeModeChange],
  );

  const handleLocaleChange = useCallback(
    (option: string) => {
      if (option && onLocaleChange) {
        onLocaleChange(option);
      }
    },
    [onLocaleChange],
  );

  return (
    <div css={styles.container}>
      {isActiveUser && (
        <>
          {isInternationalizationEnabled && (
            <>
              <div css={styles.item}>
                <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
                  <FormattedMessage {...messages.LANGUAGE_LABEL} />
                </Typography>

                <div css={styles.data}>
                  <Translate fill={[theme.colors.gray[500], theme.colors.gray[50]]} />

                  <div css={styles.select}>
                    <Select
                      options={LOCALE_OPTIONS}
                      value={currentLocaleOption.value}
                      onChange={handleLocaleChange}
                      size={SELECT_SIZES.SMALL}
                      isSearchable={false}
                      placeholder=""
                    />
                  </div>
                </div>
              </div>
              <LineSeparator />
            </>
          )}
          {isThemeModeEnabled && (
            <>
              <div css={styles.item}>
                <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
                  <FormattedMessage {...messages.THEME_MODE_LABEL} />
                </Typography>

                <div css={styles.data}>
                  <SwitchThemes fill={[theme.colors.gray[500], theme.colors.gray[50]]} />
                  <div css={styles.select}>
                    <Select
                      options={THEME_MODE_OPTIONS}
                      value={currentThemeModeOption.value}
                      onChange={handleThemeModeChange}
                      size={SELECT_SIZES.SMALL}
                      isSearchable={false}
                      placeholder=""
                    />
                  </div>
                </div>
              </div>

              <LineSeparator />
            </>
          )}
        </>
      )}

      <div css={styles.item}>
        <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
          <FormattedMessage {...messages.STATUS_LABEL} />
        </Typography>

        <div css={styles.data}>
          <Time fill={[theme.colors.gray[500], theme.colors.gray[50]]} />
          <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>{message}</Typography>
        </div>
      </div>

      <LineSeparator />

      <div css={styles.item}>
        <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
          <FormattedMessage {...messages.STREAKS_LABEL} />
        </Typography>

        <div css={styles.data}>
          <Fire fill={[theme.colors.secondary[400], theme.colors.warning[300]]} />
          <Typography variant={Typography.VARIANTS.BODY_SMALL_BOLD}>{nbMaxStreaks}</Typography>
        </div>
      </div>

      <LineSeparator />

      <div css={styles.item}>
        <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
          <FormattedMessage {...messages.COURSES_LABEL} />
        </Typography>

        {courses.map(({ course, accessType }) => (
          <div key={course.title} css={styles.data}>
            <Bookshelf fill={[theme.colors.gray[500], theme.colors.gray[50]]} />
            <Truncate maxLines={1}>
              <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
                {course.title}
              </Typography>
            </Truncate>

            {accessType === USER_COURSE_ACCESS_TYPES.LIMITED && (
              <Tag
                size={Tag.SIZES.SMALL}
                variant={Tag.VARIANTS.SUCCESS}
                label={intl.formatMessage(messages.COURSE_LIMITED_TAG)}
                icon={<Check />}
                light
              />
            )}
          </div>
        ))}
      </div>

      {isActiveUser && studentRef && (
        <Fragment>
          <LineSeparator />
          <div css={styles.item}>
            <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
              <FormattedMessage {...messages.STUDENT_ID_LABEL} />
            </Typography>
            <div css={styles.data}>
              <DegreeHat fill={[theme.colors.gray[500], theme.colors.gray[50]]} />
              <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>{studentRef}</Typography>
            </div>
          </div>
        </Fragment>
      )}
    </div>
  );
};

ProfileDetailsBase.displayName = 'ProfileDetails';

export const ProfileDetails = memo(ProfileDetailsBase);
